/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Spin } from 'antd'
import SectionHeader from './SectionHeader'

interface StatsRow {
  headers: string[]
  values: string[]
}

interface CareerStatsWidgetProps {
  playerStats: StatsRow | null
  goalieStats: StatsRow | null
  statsLoading: boolean
  isGenericSport: boolean
  sportName: string | null
  onViewAll?: () => void
}

const tableContainerStyle = css`
  border: 1px solid ${colors.SECONDARY_300};
  border-radius: 8px;
  overflow: hidden;
`

const headerRowStyle = css`
  display: flex;
  gap: 40px;
  padding: 4px 16px;
  min-height: 26px;
  align-items: center;
  background-color: ${colors.SECONDARY_300};
`

const valueRowStyle = css`
  display: flex;
  gap: 40px;
  padding: 8px 16px;
  min-height: 38px;
  align-items: center;
  background-color: ${colors.SECONDARY_500};
`

const cellStyle = css`
  flex: 1;
  text-align: center;
  min-width: 40px;
`

const StatsTable = ({ stats, label }: { stats: StatsRow; label?: string }) => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 8px;
      `}
    >
      {label && (
        <span
          css={css`
            font-family: 'Inter', sans-serif;
            font-weight: 600;
            font-size: 14px;
            color: ${colors.NEUTRAL_0};
          `}
        >
          {label}
        </span>
      )}
      <div css={tableContainerStyle}>
        <div css={headerRowStyle}>
          {stats.headers.map((header) => (
            <span
              key={header}
              css={[
                cellStyle,
                css`
                  font-family: 'Inter', sans-serif;
                  font-weight: 500;
                  font-size: 12px;
                  color: ${colors.NEUTRAL_70};
                `,
              ]}
            >
              {header}
            </span>
          ))}
        </div>
        <div css={valueRowStyle}>
          {stats.values.map((value, idx) => (
            <span
              key={stats.headers[idx]}
              css={[
                cellStyle,
                css`
                  font-family: 'Inter', sans-serif;
                  font-weight: 400;
                  font-size: 14px;
                  color: ${colors.NEUTRAL_0};
                `,
              ]}
            >
              {value}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

const CareerStatsWidget = ({
  playerStats,
  goalieStats,
  statsLoading,
  isGenericSport,
  sportName,
  onViewAll,
}: CareerStatsWidgetProps) => {
  const goalieLabel = sportName === 'soccer' ? 'Keeper' : 'Goalie'

  return (
    <div
      css={css`
        background-color: ${colors.SECONDARY_400};
        border-radius: 8px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        height: 100%;
      `}
    >
      <SectionHeader title='Career Stats' onViewAll={onViewAll} />

      {statsLoading && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 80px;
          `}
        >
          <Spin size='small' />
        </div>
      )}

      {!statsLoading && playerStats && <StatsTable stats={playerStats} />}

      {!statsLoading && !isGenericSport && goalieStats && (
        <StatsTable stats={goalieStats} label={goalieLabel} />
      )}

      {!statsLoading && !playerStats && !goalieStats && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            flex: 1;
            min-height: 60px;
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            color: ${colors.NEUTRAL_50};
          `}
        >
          No statistics available
        </div>
      )}
    </div>
  )
}

export default CareerStatsWidget
